import {
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ModalCloseButton from 'components/modal-close-button';
import ResetButton from 'components/reset-button';
import React from 'react';
import { useBreakpoints } from 'utils/use-breakpoints';
import {
  CustomisableTrait,
  getTraitColumns,
  normaliseVisibleTraitIds,
  TraitScenarioOption,
  TraitSelectionId,
} from './customise-traits-utils';
import './customise-traits.scss';

export interface CustomiseTraitsProps<T extends CustomisableTrait> {
  availableTraits: T[];
  selectedTraitIds: TraitSelectionId[];
  defaultTraitIds: TraitSelectionId[];
  onCancel: () => void;
  onSave: (selectedTraitIds: TraitSelectionId[]) => void;
  maxTraits?: number;
  title?: string;
  scenarios?: TraitScenarioOption[];
  selectedScenarioId?: number | null;
  onScenarioChange?: (scenarioId: number) => Promise<TraitSelectionId[]> | TraitSelectionId[];
}

export const CustomiseTraits = <T extends CustomisableTrait>({
  availableTraits,
  selectedTraitIds,
  defaultTraitIds,
  onCancel,
  onSave,
  maxTraits = 20,
  title = 'Customise traits',
  scenarios = [],
  selectedScenarioId,
  onScenarioChange,
}: CustomiseTraitsProps<T>) => {
  const { phone } = useBreakpoints();
  const [selectedIds, setSelectedIds] = React.useState<TraitSelectionId[]>(
    normaliseVisibleTraitIds(availableTraits, selectedTraitIds).slice(0, maxTraits),
  );
  const [currentScenarioId, setCurrentScenarioId] = React.useState<number | ''>(selectedScenarioId || '');
  const [loadingScenarioTraits, setLoadingScenarioTraits] = React.useState(false);

  React.useEffect(() => {
    setSelectedIds(normaliseVisibleTraitIds(availableTraits, selectedTraitIds).slice(0, maxTraits));
  }, [availableTraits, maxTraits, selectedTraitIds]);

  React.useEffect(() => {
    setCurrentScenarioId(selectedScenarioId || '');
  }, [selectedScenarioId]);

  const selectedTraits = React.useMemo(() => selectedIds
    .map((traitId) => availableTraits.find((trait) => trait.traitId === traitId))
    .filter((trait): trait is T => Boolean(trait)), [availableTraits, selectedIds]);
  const columns = React.useMemo(() => getTraitColumns(maxTraits).map((visible, index) => (
    visible || selectedTraits.length > 8 || index < selectedTraits.length
  )), [maxTraits, selectedTraits.length]);
  const resetIds = React.useMemo(() => normaliseVisibleTraitIds(availableTraits, defaultTraitIds).slice(0, maxTraits), [
    availableTraits,
    defaultTraitIds,
    maxTraits,
  ]);
  const resetDisabled = selectedIds.length === resetIds.length
    && selectedIds.every((traitId, index) => traitId === resetIds[index]);

  const updateSelectedTraits = (_event: React.ChangeEvent<{}>, selected: T[]) => {
    setSelectedIds(selected.slice(0, maxTraits).map((trait) => trait.traitId));
  };

  const changeScenario = async (event: React.ChangeEvent<{ value: unknown }>) => {
    const scenarioId = Number(event.target.value);

    if (!Number.isFinite(scenarioId) || !onScenarioChange) {
      return;
    }

    setCurrentScenarioId(scenarioId);
    setLoadingScenarioTraits(true);

    try {
      const scenarioTraitIds = await onScenarioChange(scenarioId);
      setSelectedIds(normaliseVisibleTraitIds(availableTraits, scenarioTraitIds).slice(0, maxTraits));
    } finally {
      setLoadingScenarioTraits(false);
    }
  };

  const deleteChip = (traitId: TraitSelectionId) => {
    setSelectedIds((current) => current.filter((selectedId) => selectedId !== traitId));
  };

  const reset = () => {
    setSelectedIds(resetIds);
  };

  const save = () => {
    onSave(selectedIds);
  };

  return (
    <div className="sg-customise-traits">
      <DialogTitle className="customise-traits__title">
        {phone && <span className="customise-traits__heading customise-traits__heading--phone">{title}</span>}
        {!phone && (
          <>
            <span className="customise-traits__heading">{title}</span>
            <ResetButton
              disabled={resetDisabled}
              onClick={reset}
            >
              Reset to default traits
            </ResetButton>
          </>
        )}
      </DialogTitle>
      <ModalCloseButton onClose={onCancel} />
      <DialogContent>
        <div className="chips__disclaimer">A maximum of {maxTraits} traits can be selected. Click the 'X' to remove a trait.</div>
        {scenarios.length > 0 && (
          <FormControl
            className="traits__scenario-container"
            variant="outlined"
          >
            <InputLabel id="customise-traits-scenario-label">Scenario</InputLabel>
            <Select
              labelId="customise-traits-scenario-label"
              value={currentScenarioId}
              onChange={changeScenario}
              label="Scenario"
              disabled={scenarios.length === 1 || loadingScenarioTraits}
            >
              {scenarios.map((scenario) => (
                <MenuItem
                  key={scenario.scenarioId}
                  value={scenario.scenarioId}
                >
                  {scenario.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <div className="chips__table">
          {columns.map((column, index) => (
            <React.Fragment key={`Column-${index + 1}`}>
              {column && (
                <div className="chip__col">
                  <div className="chip__head">{!phone && 'Column'} {index + 1}</div>
                  <div className="chip__cell">
                    {selectedTraits[index] && (
                      <Chip
                        className="trait__chip"
                        label={selectedTraits[index].abbrev}
                        onDelete={() => deleteChip(selectedTraits[index].traitId)}
                      />
                    )}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
          {!phone && selectedTraits.length > 8 && (
            <div className="table__scroll-disclaimer">These columns will appear as you scroll to the right</div>
          )}
        </div>
        {phone && (
          <div className="traits__reset">
            <ResetButton
              variant="button"
              disabled={resetDisabled}
              onClick={reset}
            >
              Reset to default traits
            </ResetButton>
          </div>
        )}
        <Grid
          className="traits__dropdown-container"
          container
          item
          sm={8}
        >
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={availableTraits}
            value={selectedTraits}
            getOptionDisabled={(option) => selectedTraits.length >= maxTraits && !selectedIds.includes(option.traitId)}
            getOptionLabel={(trait) => `${trait.traitName} (${trait.abbrev})`}
            getOptionSelected={(option, value) => option.traitId === value.traitId}
            onChange={updateSelectedTraits}
            renderOption={(option, { selected }) => (
              <span>
                <Checkbox
                  checked={selected}
                  color="primary"
                />
                {`${option.traitName} (${option.abbrev})`}
              </span>
            )}
            renderTags={() => null}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Traits"
                placeholder="Search for trait"
                variant="outlined"
              />
            )}
          />
        </Grid>
      </DialogContent>
      <DialogActions className="customise-traits__actions">
        <Button
          variant="outlined"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          onClick={save}
          disabled={selectedIds.length === 0}
        >
          Save
        </Button>
      </DialogActions>
    </div>
  );
};
